/** Panel: estado del último snapshot publicado y sus cifras de control. */

import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { useAsync } from "../api/useAsync";
import type { Config } from "../api/types";
import { ErrorState, Loading } from "../components/States";
import { defaultPeriod } from "../components/period";
import { StatTile } from "../components/StatTile";
import { Sparkline } from "../components/viz/Sparkline";
import { LineasTiempo } from "../components/viz/LineasTiempo";
import { StatusBadge } from "../components/StatusBadge";
import { runLabel, runTone } from "../components/tones";
import { DownloadButton } from "../components/DownloadButton";
import {
  formatCompact,
  formatDateTime,
  formatMillions,
  formatMonth,
  formatMoney,
  formatShare,
  formatVariation,
  toNumber,
} from "../components/format";

/**
 * Un estado financiero no es un reporte tabular: vive en otra ruta.
 *
 * Apuntan al estado OFICIAL del paquete, no a las claves del catálogo. Esas
 * —`statement_financial_position` e `income_statement`— son la conciliación
 * técnica del mapping, y desde que se movieron a `/audit` estos enlaces
 * ofrecían el nombre del documento oficial y abrían la vista técnica.
 */
const STATEMENTS = [
  {
    key: "statement_financial_position",
    label: "Estado de Situación Financiera",
    to: "/closing/esf",
  },
  { key: "income_statement", label: "Estado del Resultado Integral", to: "/closing/eri" },
];

export function Dashboard({ config }: { config: Config }) {
  const { data, error, loading } = useAsync(() => api.overview(), []);
  const [period, setPeriod] = useState(defaultPeriod);

  if (loading) return <Loading what="el último snapshot" />;
  if (error)
    return (
      <ErrorState
        error={error}
        hint="Ejecute una sincronización desde «Corridas» para poblar los datasets."
      />
    );
  if (!data) return null;

  const {
    run,
    totals,
    headline,
    exceptions_by_severity: bySeverity,
    synthetic_columns: synthetic,
  } = data;
  const blocking = bySeverity.blocking ?? 0;
  const warnings = bySeverity.warning ?? 0;

  // Dos comprobaciones distintas, y ninguna es una cifra que informe: son
  // binarias. La partida doble dice que el libro no está roto; la ecuación
  // contable dice que la clasificación externa no perdió ni duplicó nada por
  // el camino. Solo cuando fallan enseñan su importe.
  const entryDifference = toNumber(totals.difference) ?? 0;
  const doubleEntryHolds = Math.abs(entryDifference) < 0.01;
  const equationDifference =
    (toNumber(headline.assets) ?? 0) - (toNumber(headline.liabilities_and_equity) ?? 0);
  const equationHolds = Math.abs(equationDifference) < 0.01;

  // Las tres masas mes a mes, del mismo snapshot que las tarjetas: viajan en el
  // propio `overview` para que la línea y la cifra no puedan venir de dos
  // corridas distintas.
  const puntos = data.headline_series ?? [];
  const meses = puntos.map((p) => p.period_month);
  const serie = (clave: "assets" | "liabilities" | "equity") =>
    puntos.map((p) => p[clave]);
  const priorPeriod = headline.prior_as_of ? formatMonth(headline.prior_as_of) : undefined;
  const span = `${formatMonth(headline.since)} – ${formatMonth(headline.as_of)}`;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Panel</h1>
          <p>Cifras del último snapshot publicado, con su procedencia completa.</p>
        </div>
        <StatusBadge tone={runTone(run.status)}>{runLabel(run.status)}</StatusBadge>
      </div>

      {/* Antes esto vivía dentro de «Procedencia» y había que ir a buscarlo. */}
      <div className="provenance">
        <span>Fuente: {config.source.database}</span>
        <span className="sep">│</span>
        <span>Modo solo lectura</span>
        <span className="sep">│</span>
        <span>Última sincronización {formatDateTime(run.started_at)}</span>
        <span className="sep">│</span>
        <span>
          mapping {run.mapping_version}
          {config.mapping.is_draft && " · borrador"}
        </span>
      </div>

      {!doubleEntryHolds && (
        <div className="card banner critical">
          <StatusBadge tone="critical">Descuadre</StatusBadge>
          <div className="banner-body">
            <strong>Débitos y créditos no coinciden</strong>
            <p>
              Diferencia de {formatMoney(entryDifference)} en el libro posteado. Ninguna
              corrida con este descuadre debería promoverse a reportes.
            </p>
          </div>
        </div>
      )}

      {!equationHolds && (
        <div className="card banner critical">
          <StatusBadge tone="critical">Ecuación rota</StatusBadge>
          <div className="banner-body">
            <strong>El activo no iguala pasivo más patrimonio</strong>
            <p>
              Diferencia de {formatMoney(equationDifference)}. El libro puede estar
              cuadrado y aun así fallar aquí: eso apunta al mapping —una cuenta sin
              renglón, o contada dos veces—, no al libro de origen.
            </p>
          </div>
        </div>
      )}

      {/* Las masas contables van PRIMERO.
          El panel abría con apuntes, debe, haber y diferencia: la salud del
          libro. Para quien abre esta pantalla la primera pregunta es cuánto hay
          y hacia dónde va; si el libro cuadra es una comprobación, y una
          comprobación que casi siempre pasa no puede ocupar el primer bloque.

          Cada masa lleva su tendencia. `StatTile` declaraba «sin gráfica»
          porque la respuesta correcta era el número; la línea no la sustituye,
          responde la otra mitad —de dónde viene—, y va en tinta neutra para no
          afirmar que subir sea bueno. Lo que NO se revierte es la variación sin
          color: en un balance no hay una dirección buena. */}
      <div className="tiles">
        <StatTile
          mass
          label="Activo"
          value={formatMillions(headline.assets)}
          delta={formatVariation(headline.assets, headline.assets_prior)}
          deltaBase={priorPeriod}
          note={`USD ${formatMoney(headline.assets)}`}
        >
          <Sparkline valores={serie("assets")} etiqueta="el activo" />
        </StatTile>
        <StatTile
          mass
          label="Pasivo"
          value={formatMillions(headline.liabilities)}
          delta={formatVariation(headline.liabilities, headline.liabilities_prior)}
          deltaBase={priorPeriod}
          note={`${formatShare(headline.liabilities, headline.assets) ?? "—"} del activo`}
        >
          <Sparkline valores={serie("liabilities")} etiqueta="el pasivo" />
        </StatTile>
        <StatTile
          mass
          label="Patrimonio"
          value={formatMillions(headline.equity)}
          delta={formatVariation(headline.equity, headline.equity_prior)}
          deltaBase={priorPeriod}
          note={`${
            formatShare(headline.equity, headline.assets) ?? "—"
          } del activo · incluye el resultado`}
        >
          <Sparkline valores={serie("equity")} etiqueta="el patrimonio" />
        </StatTile>
        {/* El ejercicio va EN el rótulo. Esta tarjeta decía «Resultado del
            período» sobre una cifra que sumaba 2025 y 2026 —ocho veces la del
            ejercicio—, y el pie de abajo habla de saldos acumulados, que es lo
            correcto para las otras tres y lo contrario de lo que mide ésta. */}
        <StatTile
          mass
          label={
            headline.inicio_ejercicio
              ? `Resultado de ${new Date(headline.inicio_ejercicio).getUTCFullYear()}`
              : "Resultado del ejercicio"
          }
          value={formatMillions(headline.profit)}
          note={`${formatShare(headline.profit, headline.revenue) ?? "—"} de ${formatMillions(
            headline.revenue,
          )} en ingresos del ejercicio`}
        />
      </div>

      <div className="pill-row tiles-footnote">
        <StatusBadge tone={equationHolds ? "good" : "critical"}>
          {equationHolds
            ? "Ecuación: activo = pasivo + patrimonio"
            : `Ecuación: ${formatMoney(equationDifference)}`}
        </StatusBadge>
        <span className="small muted">
          Saldos acumulados de {formatMonth(headline.since)} a {formatMonth(headline.as_of)}
          {priorPeriod && ` · comparativo al cierre de ${priorPeriod}`}
        </span>
      </div>

      {/* Y la salud del libro DESPUÉS de la evolución: Debe y Haber son una
          comprobación, no un indicador, y compitiendo con Activo y Pasivo por
          el primer bloque hacían que las cuatro cifras pesaran lo mismo. */}
      <div className="card-columns panel-evolucion">
        <div className="card">
          <h2>Evolución de las masas contables</h2>
          <LineasTiempo
            meses={meses}
            series={[
              { id: "assets", label: "Activo", slot: 1, valores: serie("assets") },
              { id: "liabilities", label: "Pasivo", slot: 2, valores: serie("liabilities") },
              { id: "equity", label: "Patrimonio", slot: 3, valores: serie("equity") },
            ]}
          />
          <p className="small muted">
            Saldos acumulados al cierre de cada mes. El detalle por cuenta está en{" "}
            <Link to="/reports/trial_balance">el Balance de Comprobación</Link>.
          </p>
        </div>

        <div className="card">
          <h2>Estado del cierre</h2>
          <div className="rows">
            <div className="row-item">
              <div>
                <b>Debe = haber</b>
                <span>{formatMoney(entryDifference)} de diferencia</span>
              </div>
              <div className="row-val">
                <StatusBadge tone={doubleEntryHolds ? "good" : "critical"}>
                  {doubleEntryHolds ? "Correcto" : "Descuadre"}
                </StatusBadge>
              </div>
            </div>
            <div className="row-item">
              <div>
                <b>Ecuación contable</b>
                <span>activo = pasivo + patrimonio</span>
              </div>
              <div className="row-val">
                <StatusBadge tone={equationHolds ? "good" : "critical"}>
                  {equationHolds ? "Correcto" : "Rota"}
                </StatusBadge>
              </div>
            </div>
            <div className="row-item">
              <div>
                <b>Última sincronización</b>
                <span>disparada por {run.triggered_by ?? "—"}</span>
              </div>
              <div className="row-val small">{formatDateTime(run.started_at)}</div>
            </div>
            <div className="row-item">
              <div>
                <b>Corrida</b>
                <span>mapping {run.mapping_version}</span>
              </div>
              <div className="row-val">
                <Link className="mono" to={`/runs/${run.sync_run_id}`}>
                  {run.sync_run_id.slice(0, 8)}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <h2 className="seccion-panel">Actividad contable</h2>
      <div className="tiles">
        <StatTile
          label="Apuntes contables"
          value={formatCompact(totals.lines)}
          note={`en ${formatCompact(totals.moves)} asientos posteados`}
        />
        <StatTile label="Total debe" value={formatMillions(totals.total_debit)} note={span} />
        <StatTile label="Total haber" value={formatMillions(totals.total_credit)} note={span} />
        <StatTile label="Diferencia" value={formatMoney(entryDifference)}>
          <div style={{ marginTop: 6 }}>
            <StatusBadge tone={doubleEntryHolds ? "good" : "critical"}>
              {doubleEntryHolds ? "Cuadre correcto" : "Requiere revisión"}
            </StatusBadge>
          </div>
        </StatTile>
      </div>

      <div className="card-columns">
        <div className="card">
          {/* Sin el distintivo de estado: el encabezado de la página ya lo
              lleva, y el mismo rótulo dos veces en la misma pantalla no informa
              una segunda vez. */}
          <h2>Estado de sincronización</h2>
          <div className="rows">
            <div className="row-item">
              <div className="row-key">
                <b>Última sincronización</b>
                <span>disparada por {run.triggered_by ?? "—"}</span>
              </div>
              <div className="row-val">{formatDateTime(run.started_at)}</div>
            </div>
            <div className="row-item">
              <div className="row-key">
                <b>Origen de datos</b>
                <span>{config.source.access}</span>
              </div>
              <div className="row-val">{config.source.database}</div>
            </div>
            <div className="row-item">
              <div className="row-key">
                <b>Datos servidos</b>
                {/* Se nombran los dos: dónde se calcula y de dónde se lee.
                    Para quien audita un estado, «de qué corrida salió esta
                    cifra» y «quién la calculó» son la misma pregunta. */}
                <span>
                  {config.datasets.serving} · calculado en {config.datasets.warehouse}
                </span>
              </div>
              <div className="row-val">
                <StatusBadge tone="good">Publicado</StatusBadge>
              </div>
            </div>
            <div className="row-item">
              <div className="row-key">
                <b>Corrida</b>
                <span>mapping {run.mapping_version}</span>
              </div>
              <div className="row-val">
                <Link className="mono" to={`/runs/${run.sync_run_id}`}>
                  {run.sync_run_id.slice(0, 8)}
                </Link>
              </div>
            </div>
          </div>
          <div className="pill-row" style={{ marginTop: 12 }}>
            <Link className="btn primary" to="/runs">
              Sincronizar ahora
            </Link>
            <Link className="btn" to="/runs">
              Ver historial
            </Link>
          </div>
        </div>

        <div className="card">
          <h2>
            Validaciones contables
            <Link className="small" to="/exceptions">
              Ver detalle
            </Link>
          </h2>
          <div className="rows">
            <div className="row-item">
              <div className="row-key">
                <b>Debe = haber</b>
                <span>la suma del debe y el haber es igual</span>
              </div>
              <StatusBadge tone={doubleEntryHolds ? "good" : "critical"}>
                {doubleEntryHolds ? "Correcto" : "Descuadre"}
              </StatusBadge>
            </div>
            <div className="row-item">
              <div className="row-key">
                <b>Ecuación contable</b>
                <span>activo = pasivo + patrimonio</span>
              </div>
              <StatusBadge tone={equationHolds ? "good" : "critical"}>
                {equationHolds ? "Correcto" : "Rota"}
              </StatusBadge>
            </div>
            {/* Decía «Excepciones bloqueantes — impiden publicar una corrida»,
                y era falso: hay DOS conceptos con el mismo nombre y este es el
                otro.

                Una VALIDACIÓN bloqueante sí impide publicar, y por eso una
                corrida que la tenga nunca llega a `published` — `jobs/sync.py`
                la manda a `failed` y las vistas `current_*` vuelven a la última
                publicada. Para la corrida que este panel muestra, ese número es
                siempre cero.

                Lo que se cuenta aquí son EXCEPCIONES de calidad con severidad
                `blocking`: defectos por documento en el origen, que ninguna
                regla convierte en fallo de publicación —las dos que leen esa
                vista son `WARNING`—. Así que la pantalla afirmaba, a la vez,
                que la corrida está publicada y que hay 193 cosas que impiden
                publicarla. El rótulo era el defecto, no la cifra. */}
            <div className="row-item">
              <div className="row-key">
                <b>Excepciones críticas de datos</b>
                <span>defectos por documento en el origen</span>
              </div>
              <StatusBadge tone={blocking > 0 ? "serious" : "good"}>
                {blocking > 0 ? `${blocking} críticas` : "Ninguna"}
              </StatusBadge>
            </div>
            <div className="row-item">
              <div className="row-key">
                <b>Advertencias de calidad</b>
                <span>cuentas sin mapear, huecos del histórico</span>
              </div>
              <StatusBadge tone={warnings > 0 ? "serious" : "good"}>
                {warnings > 0 ? `${warnings} advertencias` : "Ninguna"}
              </StatusBadge>
            </div>
          </div>
          <p className="small muted" style={{ marginBottom: 0, marginTop: 10 }}>
            Los huecos del histórico fiscal se reportan aquí; nunca se rellenan en el origen.
          </p>
        </div>
      </div>

      <div className="card-columns">
        <div className="card">
          {/* Era la lista COMPLETA de reportes, con un «Ver» por renglón: el
              menú lateral entero repetido a media pantalla de distancia y sin
              un dato más. Lo que sí falta en el menú son los dos estados que se
              firman, así que quedan ellos y nada más. */}
          <h2>Estados financieros</h2>
          <div className="rows">
            {STATEMENTS.map((statement) => (
              <div className="row-item" key={statement.key}>
                <div className="row-key">
                  <b>{statement.label}</b>
                </div>
                <div className="row-val">
                  <Link to={statement.to}>Ver</Link>
                </div>
              </div>
            ))}
          </div>
          <p className="small muted" style={{ marginBottom: 0, marginTop: 10 }}>
            Los libros, los anexos de IVA y el Balance de Comprobación están en el
            menú de la izquierda.
          </p>
        </div>

        <div className="card">
          <h2>Descargar el período completo</h2>
          <p className="small muted" style={{ marginTop: 0 }}>
            Un solo libro de Excel con los estados financieros, los libros, los anexos
            F-07 y las excepciones: una hoja por reporte y una portada con la
            procedencia.
          </p>
          <div className="pill-row">
            <DownloadButton
              variant="primary"
              href={api.workbookUrl({ periodTo: period.periodTo })}
            >
              Descargar todo (XLSX)
            </DownloadButton>
            <input
              type="date"
              aria-label="Hasta"
              value={period.periodTo}
              onChange={(event) => setPeriod({ ...period, periodTo: event.target.value })}
            />
          </div>
          <dl className="facts small" style={{ marginTop: 14 }}>
            <dt>Período con datos</dt>
            <dd>
              {totals.period_from ?? "—"} a {totals.period_to ?? "—"}
            </dd>
            <dt>Origen</dt>
            <dd className="mono">
              {config.source.database} · usuario {config.source.user}
            </dd>
            <dt>Acceso</dt>
            <dd>{config.source.access}</dd>
          </dl>
        </div>
      </div>

      {synthetic.length > 0 && (
        <div className="card">
          <h2>Columnas ausentes en este ambiente</h2>
          <p className="small muted" style={{ marginTop: 0 }}>
            Estas columnas las declara el pipeline pero no existen en el esquema de{" "}
            {config.source.database}: se cargan como NULL para que RAW tenga la misma
            forma en todos los ambientes. No se inventa ningún valor.
          </p>
          <div className="pill-row">
            {synthetic.map((column) => (
              <span key={column} className="mono small">
                {column}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Material de administración: el conteo por tabla RAW no es la primera
          pantalla de quien cierra un mes, así que queda al final y plegado. */}
      <details className="card">
        <summary className="small muted" style={{ cursor: "pointer" }}>
          Filas por tabla RAW · {Object.keys(data.table_counts).length} tablas
        </summary>
        <div className="table-wrap" style={{ marginTop: 12 }}>
          <table>
            <thead>
              <tr>
                <th scope="col">Tabla RAW</th>
                <th scope="col" className="num">
                  Filas
                </th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(data.table_counts)
                .sort(([, a], [, b]) => b - a)
                .map(([name, count]) => (
                  <tr key={name}>
                    <td className="mono">{name}</td>
                    <td className="num">{formatCompact(count)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </details>
    </>
  );
}
